import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ChevronRight, ChevronLeft, Timer, Pause, Play, X, BellRing } from 'lucide-react';
import { IngredientPopup } from './IngredientPopup';
import { useWakeLock } from '../hooks/useWakeLock';
import { parseTimeDurations, formatTimeRemaining } from '../utils/timeParser';

export function StepByStepCooking({ recipe, method, ingredients, onBack }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const { isSupported, requestWakeLock, releaseWakeLock } = useWakeLock();
  const [activeTimers, setActiveTimers] = useState([]);
  // Finished timers stay on screen until dismissed: easy to miss a sound or a passing message
  // while busy in a noisy kitchen. No toasts.
  const [finishedTimers, setFinishedTimers] = useState([]);
  const timerIntervalsRef = useRef(new Map());
  const audioContextRef = useRef(null);

  const totalSteps = method.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Request wake lock on mount
  useEffect(() => {
    if (isSupported) {
      requestWakeLock();
    }
    // Cleanup: release wake lock when component unmounts
    return () => {
      releaseWakeLock();
    };
  }, [isSupported, requestWakeLock, releaseWakeLock]);


  // Timer countdown effect - manages all active timers
  useEffect(() => {
    const intervalsMap = timerIntervalsRef.current;

    // Clear all intervals first
    intervalsMap.forEach((interval) => {
      clearInterval(interval);
    });
    intervalsMap.clear();

    // Set up intervals for each active, non-paused timer
    activeTimers.forEach((timer) => {
      if (!timer.isPaused) {
        // Only count down here; finishing is handled by the effect below, so the sound and the
        // alert never run inside a state update (React may call updaters twice).
        const interval = setInterval(() => {
          setActiveTimers((prevTimers) => prevTimers.map((t) => (
            t.id === timer.id ? { ...t, remaining: t.remaining - 1 } : t
          )));
        }, 1000);

        intervalsMap.set(timer.id, interval);
      }
    });

    return () => {
      intervalsMap.forEach((interval) => {
        clearInterval(interval);
      });
      intervalsMap.clear();
    };
  }, [activeTimers]);

  // When a timer reaches zero: sound/vibrate once, move it to the persistent "done" alerts.
  useEffect(() => {
    const done = activeTimers.filter((t) => t.remaining <= 0);
    if (!done.length) return;
    playTimerAlertSound();
    setFinishedTimers((prev) => [
      ...prev,
      ...done.map((t) => ({ id: t.id, stepLabel: t.stepLabel, label: t.label })),
    ]);
    setActiveTimers((prev) => prev.filter((t) => t.remaining > 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimers]);

  const dismissFinishedTimer = (timerId) => {
    setFinishedTimers((prev) => prev.filter((t) => t.id !== timerId));
  };

  // Play alert sound when timer completes
  const playTimerAlertSound = async () => {
    try {
      // Try to vibrate on mobile devices
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200, 100, 400]); // Pattern: short-pause-short-pause-long
      }
      
      // Use the AudioContext that was created on user interaction.
      // This is important for iOS/Android where creating it lazily on
      // timer completion (without a direct gesture) can be blocked.
      const audioContext = audioContextRef.current;

      if (!audioContext) {
        console.log('AudioContext not initialized; skipping timer alert sound');
        return;
      }
      
      // Resume AudioContext if suspended (required for mobile)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      // Play a louder, higher-pitched multi-beep alert
      const playBeep = (frequency, startTime, duration, volume = 0.6) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        // Square wave cuts through background noise better on phones
        oscillator.type = 'square';
        
        const startAt = audioContext.currentTime + startTime;
        gainNode.gain.setValueAtTime(volume, startAt);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
        
        oscillator.start(startAt);
        oscillator.stop(startAt + duration);
      };
      
      // Sequence of high-pitched beeps over ~1.5 seconds
      playBeep(1800, 0, 0.25);
      playBeep(2100, 0.35, 0.25);
      playBeep(2400, 0.7, 0.25);
      playBeep(2100, 1.05, 0.35);
      
    } catch (error) {
      console.log('Could not play timer alert sound:', error);
    }
  };

  const handleNext = () => {
    // Close popover when changing steps
    setSelectedIngredient(null);
    setPopoverPosition(null);
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Finish cooking
      onBack();
    }
  };

  const handlePrevious = () => {
    // Close popover when changing steps
    setSelectedIngredient(null);
    setPopoverPosition(null);
    
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleProgressClick = (index) => {
    // Close popover when changing steps
    setSelectedIngredient(null);
    setPopoverPosition(null);
    setCurrentStep(index);
  };

  // Parse the current step text and identify ingredients
  const renderStepWithIngredients = (stepText) => {
    // Split the step into words and check each for ingredient matches
    const words = stepText.split(' ');
    const elements = [];
    let currentText = '';

    // Descriptor words to ignore at the start of multi-word ingredient names
    const descriptorWords = [
      'fresh',
      'freshly',
      'sea',
      'black',
      'white',
      'smoked',
      'dried',
      'ground',
      'coarse',
      'fine',
      'cracked',
      // common prep words that should not be treated as the core ingredient
      'grated',
      'chopped',
      'diced',
      'sliced',
      'minced'
    ];

    const handleIngredientClick = (ingredient) => (event) => {
      const rect = event.target.getBoundingClientRect();
      setPopoverPosition({
        x: rect.left,
        y: rect.bottom,
      });
      setSelectedIngredient(ingredient);
    };
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const wordLower = word.toLowerCase().replace(/[.,]/g, '');
      
      // Check if this word (or combination of words) matches an ingredient
      let matchFound = false;
      
      for (const ingredient of ingredients) {
        const ingNameLower = ingredient.name.toLowerCase();
        
        // Check for multi-word ingredients
        if (ingNameLower.includes(' ')) {
          const ingWords = ingNameLower.split(' ');
          let potentialMatch = '';
          
          for (let j = 0; j < ingWords.length && i + j < words.length; j++) {
            const checkWord = words[i + j].toLowerCase().replace(/[.,]/g, '');
            potentialMatch += (j > 0 ? ' ' : '') + checkWord;
          }
          
          if (potentialMatch === ingNameLower) {
            // Found a match
            if (currentText) {
              elements.push(<span key={`text-${elements.length}`}>{currentText}</span>);
              elements.push(<span key={`space-before-${elements.length}`}> </span>);
              currentText = '';
            }
            
            const baseIndex = i;
            const matchedWords = ingWords.map((_, idx) => words[baseIndex + idx]).join(' ');
            elements.push(
              <button
                key={`ing-${elements.length}`}
                onClick={handleIngredientClick(ingredient)}
                className="underline transition-colors"
                style={{ 
                  fontSize: 'inherit', 
                  lineHeight: 'inherit', 
                  fontWeight: 'inherit', 
                  fontFamily: 'inherit',
                  color: '#667D3F'
                }}
              >
                {matchedWords}
              </button>
            );
            elements.push(<span key={`space-${elements.length}`}> </span>);
            
            i += ingWords.length - 1; // Skip the words we just processed
            matchFound = true;
            break;
          }

          // Fallback: allow matching on a "core" ingredient word (e.g. "fresh dill" → "dill")
          const coreTokens = ingWords.filter((w) => {
            const cleaned = w.replace(/[.,]/g, '').trim().toLowerCase();
            return cleaned && !descriptorWords.includes(cleaned);
          });

          // Prefer the last non-descriptor token as the core ingredient
          // e.g. "Button mushroom" → "mushroom", "Pumpkin, grated" → "pumpkin"
          const rawBaseToken = coreTokens[coreTokens.length - 1] || ingWords[ingWords.length - 1] || '';
          const baseToken = rawBaseToken.replace(/[.,]/g, '').trim();

          if (baseToken) {
            const base = baseToken.toLowerCase();
            if (
              wordLower === base ||
              wordLower === base + 's' ||
              wordLower + 's' === base
            ) {
              if (currentText) {
                elements.push(<span key={`text-${elements.length}`}>{currentText}</span>);
                elements.push(<span key={`space-before-${elements.length}`}> </span>);
                currentText = '';
              }

              elements.push(
                <button
                  key={`ing-${elements.length}`}
                  onClick={handleIngredientClick(ingredient)}
                  className="underline transition-colors"
                  style={{ 
                    fontSize: 'inherit', 
                    lineHeight: 'inherit', 
                    fontWeight: 'inherit', 
                    fontFamily: 'inherit',
                    color: '#667D3F'
                  }}
                >
                  {word}
                </button>
              );
              elements.push(<span key={`space-${elements.length}`}> </span>);
              matchFound = true;
              break;
            }
          }
        } else if (wordLower === ingNameLower || wordLower === ingNameLower + 's' || wordLower + 's' === ingNameLower) {
          // Single word ingredient match
          if (currentText) {
            elements.push(<span key={`text-${elements.length}`}>{currentText}</span>);
            elements.push(<span key={`space-before-${elements.length}`}> </span>);
            currentText = '';
          }
          
          elements.push(
            <button
              key={`ing-${elements.length}`}
              onClick={handleIngredientClick(ingredient)}
              className="underline transition-colors"
              style={{ 
                fontSize: 'inherit', 
                lineHeight: 'inherit', 
                fontWeight: 'inherit', 
                fontFamily: 'inherit',
                color: '#667D3F'
              }}
            >
              {word}
            </button>
          );
          elements.push(<span key={`space-${elements.length}`}> </span>); // Add space after ingredient
          
          matchFound = true;
          break;
        }
      }
      
      if (!matchFound) {
        currentText += (currentText ? ' ' : '') + word;
      } else {
        currentText += ' '; // Add space after ingredient
      }
    }
    
    if (currentText) {
      elements.push(<span key={`text-${elements.length}`}>{currentText}</span>);
    }
    
    return <>{elements}</>;
  };

  // Handle timer start
  const handleTimerStart = (duration, label) => {
    // Unlock audio during this tap (mobile browsers require a user gesture), but don't wait for it:
    // the timer must start even if the unlock is slow or fails. Awaiting resume() used to leave the
    // timer unstarted whenever that promise never settled.
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
    } catch (error) {
      // No audio available; the vibration and the on-screen alert still work.
    }
    
    const newTimer = {
      id: `timer-${Date.now()}-${Math.random()}`,
      stepNumber: currentStep + 1,
      stepLabel: `Step ${currentStep + 1}`,
      duration,
      remaining: duration,
      label,
      isPaused: false,
    };
    
    setActiveTimers(prev => [...prev, newTimer]);
  };

  // Handle timer pause
  const handleTimerPause = (timerId) => {
    setActiveTimers(prev =>
      prev.map(t =>
        t.id === timerId ? { ...t, isPaused: true } : t
      )
    );
  };

  // Handle timer resume
  const handleTimerResume = (timerId) => {
    setActiveTimers(prev =>
      prev.map(t =>
        t.id === timerId ? { ...t, isPaused: false } : t
      )
    );
  };

  // Handle timer stop/cancel
  const handleTimerCancel = (timerId) => {
    setActiveTimers(prev => prev.filter(t => t.id !== timerId));
    
    // Clear the interval for this timer
    const interval = timerIntervalsRef.current.get(timerId);
    if (interval) {
      clearInterval(interval);
      timerIntervalsRef.current.delete(timerId);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-[var(--border)] sticky top-0 z-10">
        <div className="max-w-md mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Back to recipe"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl">{recipe.name}</h1>
            <p className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {totalSteps}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-md mx-auto px-6 pb-4">
          <div className="flex gap-1">
            {method.map((_, index) => (
              <button
                key={index}
                onClick={() => handleProgressClick(index)}
                className="flex-1 h-2 rounded-full overflow-hidden bg-muted hover:opacity-90 transition-colors"
                aria-label={`Go to step ${index + 1}`}
              >
                <div
                  className={`h-full transition-all duration-300 ${
                    index <= currentStep ? 'bg-primary' : 'bg-transparent'
                  }`}
                  style={{
                    width: index === currentStep ? `${progress % (100 / totalSteps)}%` : index < currentStep ? '100%' : '0%'
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Compact Timer Badges */}
        {activeTimers.length > 0 && (
          <div className="max-w-md mx-auto px-6 pb-3">
            <div className="flex flex-wrap gap-2 justify-start">
              {activeTimers.map((timer) => (
                // Bigger for reading at a distance; controls are 44px targets (WCAG 2.2: 2.5.8, and
                // the 44px the app uses everywhere). Labels name the timer, as several can run at once.
                <div
                  key={timer.id}
                  className="bg-accent rounded-full py-1 pl-4 pr-1 flex items-center gap-2 shadow-sm text-accent-foreground"
                  title={`${timer.stepLabel}: ${timer.label}`}
                >
                  <Timer className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                  <span className="font-mono text-xl whitespace-nowrap">
                    <span className="sr-only">{timer.stepLabel}, {timer.label}{timer.isPaused ? ', paused' : ''}: </span>
                    {formatTimeRemaining(timer.remaining)}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => timer.isPaused ? handleTimerResume(timer.id) : handleTimerPause(timer.id)}
                      className="w-11 h-11 rounded-full bg-card hover:bg-gray-50 flex items-center justify-center transition-colors"
                      aria-label={`${timer.isPaused ? 'Resume' : 'Pause'} timer for ${timer.stepLabel}, ${timer.label}`}
                    >
                      {timer.isPaused ? (
                        <Play className="w-5 h-5 text-accent-foreground" aria-hidden="true" />
                      ) : (
                        <Pause className="w-5 h-5 text-accent-foreground" aria-hidden="true" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTimerCancel(timer.id)}
                      className="w-11 h-11 rounded-full bg-card hover:bg-gray-50 flex items-center justify-center transition-colors"
                      aria-label={`Cancel timer for ${timer.stepLabel}, ${timer.label}`}
                    >
                      <X className="w-5 h-5 text-accent-foreground" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Finished timers: persist until dismissed. Always mounted so each new one is announced. */}
        <div role="alert" aria-live="assertive" className="max-w-md mx-auto px-6">
          {finishedTimers.map((timer) => (
            <div
              key={timer.id}
              className="mb-3 flex items-start gap-3 rounded-xl border-2 border-[#1F5D38] bg-[#EAF6EE] py-2 pl-3 pr-1 text-[#1F5D38]"
            >
              <BellRing className="mt-1 h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <p className="flex-1 py-1 text-base">
                <strong>Timer done:</strong> {timer.stepLabel}, {timer.label}.
              </p>
              <button
                type="button"
                onClick={() => dismissFinishedTimer(timer.id)}
                aria-label={`Dismiss timer done alert for ${timer.stepLabel}, ${timer.label}`}
                className="mpg-focus-ring w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center hover:bg-[#D6EEDD]"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      </header>

      {/* Step Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full">
          <div className="bg-card rounded-3xl shadow-sm p-8">
            {/* Stacked on mobile so the step text gets the full container width — the number in
                its own column cost ~64px of a ~336px line, which at text-2xl was only a few words
                per line. Side-by-side from 768px, where there's width to spare. */}
            <div className="flex flex-col md:flex-row items-start gap-3 md:gap-4 mb-6">
              {/* Smaller on mobile: stacked above the text it no longer competes for width, so the
                  vertical space it costs matters more than its size. Full size from 768px. */}
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0 text-lg md:text-xl">
                {currentStep + 1}
              </div>
              {/* w-full, not just flex-1: the parent's items-start stops children stretching, so
                  in the stacked layout this would otherwise size to its content. */}
              <div className="w-full md:flex-1">
                <p className="text-2xl leading-relaxed mb-4">
                  {renderStepWithIngredients(method[currentStep])}
                </p>
                
                {/* Timer Buttons */}
                {(() => {
                  const timeDurations = parseTimeDurations(method[currentStep]);
                  if (timeDurations.length > 0) {
                    return (
                      <div className="flex flex-wrap gap-2 mt-6">
                        {timeDurations.map((time, index) => {
                          // Check if this specific timer is already running
                          const isThisTimerActive = activeTimers.some(
                            t => t.stepNumber === currentStep + 1 && t.label === time.text
                          );
                          
                          return (
                            <button
                              key={index}
                              onClick={() => handleTimerStart(time.duration, time.text)}
                              disabled={isThisTimerActive}
                              className={`flex items-center gap-2 min-h-[44px] px-5 py-2 rounded-full transition-colors ${
                                  isThisTimerActive
                                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                                    : 'bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm'
                                }`}
                            >
                              <Timer className="w-5 h-5" aria-hidden="true" />
                              <span className="text-base">
                                {isThisTimerActive ? `${time.text} timer running` : `Start ${time.text} timer`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bg-white border-t border-gray-200 sticky bottom-0">
        <div className="max-w-md mx-auto px-6 py-6 flex justify-between gap-3">
          {currentStep > 0 ? (
            <button
              onClick={handlePrevious}
              className="px-8 py-3 bg-card text-primary-foreground border-2 border-primary rounded-full shadow-sm hover:bg-muted transition-colors flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Previous</span>
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={handleNext}
            className="px-8 py-3 bg-primary text-primary-foreground rounded-full shadow-md hover:bg-primary-hover transition-colors flex items-center gap-2"
          >
            <span>{currentStep === totalSteps - 1 ? 'Finish' : 'Next'}</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Ingredient Popup */}
      <IngredientPopup
        ingredient={selectedIngredient}
        onClose={() => setSelectedIngredient(null)}
        position={popoverPosition}
      />
    </div>
  );
}
